import React, { useState } from 'react'
import {
  HiRocketLaunch,
  HiSparkles,
  HiArrowsRightLeft,
  HiLockOpen,
  HiLockClosed,
} from 'react-icons/hi2'
import { useTheme } from '../../contexts/ThemeContext'
import { usePerformance } from '../../contexts/PerformanceContext'
import { useSidebarBehavior } from '../../contexts/SidebarBehaviorContext'
import { useSidebar } from './sidebar'
import { motion } from 'framer-motion'
import { cn } from '../../utils/formatter'
import { GlassHoverEffect } from '../GlassHoverEffect'
import { FaMoon, FaSun } from 'react-icons/fa'

type ToggleSwitcherProps = {
  value: boolean
  onToggle: () => void
  options: {
    icon: React.ReactNode
    label: string
    value: boolean
  }[]
  isCompact?: boolean
  isReady?: boolean
}

const isThemeDisabled = true
const ToggleSwitcher: React.FC<ToggleSwitcherProps> = ({
  value,
  onToggle,
  options,
  isCompact = false,
  isReady = true,
}) => {
  const { performanceMode } = usePerformance()
  const [isAnimating, setIsAnimating] = useState(false)

  const handleToggle = () => {
    setIsAnimating(true)
    onToggle()
    setTimeout(() => setIsAnimating(false), 300)
  }

  const activeIndex = value ? 1 : 0

  if (isCompact) {
    // Modo compacto - apenas a opção ativa visível com "blob" (igual navegação fechada)
    return (
      <div className='relative'>
        <GlassHoverEffect
          activeIndex={0}
          direction='vertical'
          className='relative'
          glassClassName='rounded-xl'
          compactMode={true}
          compactSize={33}
          animationConfig={{ stiffness: 500, damping: 30, mass: 0.8 }}
        >
          {({ containerRef, containerProps, getItemProps }) => (
            <div
              ref={containerRef as React.RefObject<HTMLDivElement>}
              className='flex flex-col items-center'
              {...containerProps}
            >
              <button
                {...getItemProps(0)}
                onClick={handleToggle}
                className={cn(
                  'h-[33px] w-[33px] rounded-lg flex items-center justify-center relative z-10'
                )}
                title={options[activeIndex].label}
                aria-label={options[activeIndex].label}
              >
                <span className='w-4 h-4 flex items-center justify-center'>
                  {options[activeIndex].icon}
                </span>
              </button>
            </div>
          )}
        </GlassHoverEffect>
      </div>
    )
  }

  // Modo expandido - toggle com glass effect
  return (
    <div className='relative w-full'>
      {isReady ? (
        <GlassHoverEffect
          activeIndex={activeIndex}
          direction='horizontal'
          className='relative'
          glassClassName='!rounded-md'
          compactMode={false}
          animationConfig={{
            stiffness: 500,
            damping: 30,
            mass: 0.8,
          }}
        >
          {({ containerRef, containerProps, getItemProps }) => (
            <div
              ref={containerRef as React.RefObject<HTMLDivElement>}
              className='flex gap-0.5 p-0.5 bg-white/70 dark:bg-neutral-800/70 rounded-lg border border-gray-200/60 dark:border-neutral-700/60 shadow-sm backdrop-blur-sm'
              {...containerProps}
            >
              {options.map((option, idx) => (
                <button
                  key={idx}
                  {...getItemProps(idx)}
                  onClick={handleToggle}
                  className={cn(
                    'relative flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-all duration-200 flex items-center gap-1 z-10 justify-center min-w-0',
                    value === option.value
                      ? 'text-blue-700 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-50/50 dark:hover:bg-neutral-700/50'
                  )}
                >
                  <motion.div
                    animate={{
                      scale: value === option.value && isAnimating ? [1, 1.15, 1] : 1,
                    }}
                    transition={{ duration: performanceMode === 'beauty' ? 0.25 : 0 }}
                    className='flex items-center gap-1 min-w-0'
                  >
                    <span className='flex-shrink-0'>{option.icon}</span>
                    <span className='truncate'>{option.label}</span>
                  </motion.div>
                </button>
              ))}
            </div>
          )}
        </GlassHoverEffect>
      ) : (
        <div className='flex gap-0.5 p-0.5 bg-white dark:bg-neutral-800 rounded-lg border border-gray-200/60 dark:border-neutral-700/60 shadow-sm'>
          {options.map((option, idx) => (
            <button
              key={idx}
              onClick={handleToggle}
              className={cn(
                'relative flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-all duration-200 flex items-center gap-1 z-10 justify-center min-w-0',
                value === option.value
                  ? 'text-blue-700 dark:text-blue-400 bg-white/50 dark:bg-neutral-700/50'
                  : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-50/50 dark:hover:bg-neutral-700/50'
              )}
            >
              <span className='flex-shrink-0'>{option.icon}</span>
              <span className='truncate'>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

type TripleToggleSwitcherProps = {
  value: 'auto' | 'open' | 'closed'
  onChange: (value: 'auto' | 'open' | 'closed') => void
  options: {
    icon: React.ReactNode
    label: string
    value: 'auto' | 'open' | 'closed'
  }[]
  isCompact?: boolean
  isReady?: boolean
}

const TripleToggleSwitcher: React.FC<TripleToggleSwitcherProps> = ({
  value,
  onChange,
  options,
  isCompact = false,
  isReady = true,
}) => {
  const { performanceMode } = usePerformance()
  const [isAnimating, setIsAnimating] = useState(false)

  const handleSelect = (next: 'auto' | 'open' | 'closed') => {
    setIsAnimating(true)
    onChange(next)
    setTimeout(() => setIsAnimating(false), 300)
  }

  const activeIndex = options.findIndex(option => option.value === value)

  if (isCompact) {
    // Modo compacto - apenas a opção ativa visível com blob (como navegação fechada)
    return (
      <div className='relative'>
        <GlassHoverEffect
          activeIndex={0}
          direction='vertical'
          className='relative'
          glassClassName='rounded-xl'
          compactMode={true}
          compactSize={33}
          animationConfig={{ stiffness: 500, damping: 30, mass: 0.8 }}
        >
          {({ containerRef, containerProps, getItemProps }) => (
            <div
              ref={containerRef as React.RefObject<HTMLDivElement>}
              className='flex flex-col items-center'
              {...containerProps}
            >
              <button
                {...getItemProps(0)}
                onClick={() =>
                  handleSelect(value === 'auto' ? 'open' : value === 'open' ? 'closed' : 'auto')
                }
                className={cn(
                  'h-[33px] w-[33px] rounded-lg flex items-center justify-center text-gray-600 dark:text-gray-400 relative z-10',
                  'text-blue-700 dark:text-blue-400'
                )}
                title={options[activeIndex].label}
                aria-label={options[activeIndex].label}
              >
                <span className='w-4 h-4 flex items-center justify-center'>
                  {options[activeIndex].icon}
                </span>
              </button>
            </div>
          )}
        </GlassHoverEffect>
      </div>
    )
  }

  // Modo expandido - toggle com glass effect
  return (
    <div className='relative w-full'>
      {isReady ? (
        <GlassHoverEffect
          activeIndex={activeIndex}
          direction='horizontal'
          className='relative'
          glassClassName='!rounded-md'
          compactMode={false}
          animationConfig={{
            stiffness: 500,
            damping: 30,
            mass: 0.8,
          }}
        >
          {({ containerRef, containerProps, getItemProps }) => (
            <div
              ref={containerRef as React.RefObject<HTMLDivElement>}
              className='flex gap-0.5 p-0.5 bg-white/70 dark:bg-neutral-800/70 rounded-lg border border-gray-200/60 dark:border-neutral-700/60 shadow-sm backdrop-blur-sm'
              {...containerProps}
            >
              {options.map((option, idx) => (
                <button
                  key={idx}
                  {...getItemProps(idx)}
                  onClick={() => handleSelect(option.value)}
                  className={cn(
                    'relative flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-all duration-200 flex items-center gap-1 z-10 justify-center min-w-0',
                    value === option.value
                      ? 'text-blue-700 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-50/50 dark:hover:bg-neutral-700/50'
                  )}
                >
                  <motion.div
                    animate={{
                      scale: value === option.value && isAnimating ? [1, 1.15, 1] : 1,
                    }}
                    transition={{ duration: performanceMode === 'beauty' ? 0.25 : 0 }}
                    className='flex items-center gap-1 min-w-0'
                  >
                    <span className='flex-shrink-0'>{option.icon}</span>
                    <span className='truncate'>{option.label}</span>
                  </motion.div>
                </button>
              ))}
            </div>
          )}
        </GlassHoverEffect>
      ) : (
        <div className='flex gap-0.5 p-0.5 bg-white dark:bg-neutral-800 rounded-lg border border-gray-200/60 dark:border-neutral-700/60 shadow-sm'>
          {options.map((option, idx) => (
            <button
              key={idx}
              onClick={() => handleSelect(option.value)}
              className={cn(
                'relative flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-all duration-200 flex items-center gap-1 z-10 justify-center min-w-0',
                value === option.value
                  ? 'text-blue-700 dark:text-blue-400 bg-white/50 dark:bg-neutral-700/50'
                  : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-50/50 dark:hover:bg-neutral-700/50'
              )}
            >
              <span className='flex-shrink-0'>{option.icon}</span>
              <span className='truncate'>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export const SidebarSettings: React.FC = () => {
  const { theme, toggleTheme } = useTheme()
  const { performanceMode, togglePerformanceMode } = usePerformance()
  const { sidebarBehavior, setSidebarBehavior } = useSidebarBehavior()
  // Tentar obter o contexto da sidebar de forma segura
  let open = true // Default para desktop
  try {
    const sidebar = useSidebar()
    open = sidebar.open
  } catch {
    // Se não estiver dentro do SidebarProvider, usa valor padrão
    console.warn('SidebarSettings sendo usado fora do SidebarProvider')
  }

  const isDark = theme === 'dark'
  const isBeauty = performanceMode === 'beauty'

  const themeOptions = [
    {
      icon: <FaSun className='size-5 text-amber-300 dark:text-amber-400' />,
      label: 'Claro',
      value: false,
    },
    {
      icon: <FaMoon className='size-5 text-shadow-blue-400 dark:text-shadow-blue-400' />,
      label: 'Escuro',
      value: true,
    },
  ]

  const performanceOptions = [
    {
      icon: <HiRocketLaunch className='w-4 h-4 text-emerald-500 dark:text-emerald-400' />,
      label: 'Rápido',
      value: false,
    },
    {
      icon: <HiSparkles className='w-4 h-4 text-purple-700 dark:text-purple-300' />,
      label: 'Bonito',
      value: true,
    },
  ]

  const behaviorOptions = [
    {
      icon: <HiArrowsRightLeft className='w-4 h-4 text-blue-700 dark:text-blue-400' />,
      label: 'Auto',
      value: 'auto' as const,
    },
    {
      icon: <HiLockOpen className='w-4 h-4 text-green-500 dark:text-green-400' />,
      label: 'Aberta',
      value: 'open' as const,
    },
    {
      icon: <HiLockClosed className='w-4 h-4 text-red-500 dark:text-red-400' />,
      label: 'Fechada',
      value: 'closed' as const,
    },
  ]

  return (
    <div className='border-t border-gray-200/30 dark:border-neutral-700/30'>
      {open ? (
        // Modo expandido - linha horizontal compacta com todos os controles
        <div className='flex flex-row items-center justify-around gap-1 py-3 px-2'>
          {/* Theme */}
          <div className={`${isThemeDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <ToggleSwitcher
              value={isDark}
              onToggle={isThemeDisabled ? () => {} : toggleTheme}
              options={themeOptions}
              isCompact
            />
          </div>

          <div className='h-5 w-px bg-gray-200/60 dark:bg-neutral-700/60' />

          {/* Performance */}
          <ToggleSwitcher
            value={isBeauty}
            onToggle={togglePerformanceMode}
            options={performanceOptions}
            isCompact
          />

          <div className='h-5 w-px bg-gray-200/60 dark:bg-neutral-700/60' />

          {/* Behavior */}
          <TripleToggleSwitcher
            value={sidebarBehavior}
            onChange={setSidebarBehavior}
            options={behaviorOptions}
            isCompact
          />
        </div>
      ) : (
        // Modo compacto - apenas ícones empilhados
        <div className='flex flex-col gap-3 items-center py-4 px-3'>
          <div className={`${isThemeDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <ToggleSwitcher
              value={isDark}
              onToggle={isThemeDisabled ? () => {} : toggleTheme}
              options={themeOptions}
              isCompact
            />
          </div>
          <div className='w-6 h-px bg-gray-200/60 dark:bg-neutral-700/60' />
          <ToggleSwitcher
            value={isBeauty}
            onToggle={togglePerformanceMode}
            options={performanceOptions}
            isCompact
          />
          <div className='w-6 h-px bg-gray-200/60 dark:bg-neutral-700/60' />
          <TripleToggleSwitcher
            value={sidebarBehavior}
            onChange={setSidebarBehavior}
            options={behaviorOptions}
            isCompact
          />
        </div>
      )}
    </div>
  )
}
